import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DocumentVault from "@/components/DocumentVault";
import { FolderArchive, MapPin } from "lucide-react";

/**
 * Customer Documents — plant switcher + DocumentVault per plant.
 * Uses /api/customer/summary to enumerate the customer's plants,
 * then delegates upload/list/download to the existing DocumentVault
 * component (RBAC in the backend keeps customers read-download-only
 * where required).
 */
export default function Documents() {
    const [plants, setPlants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState(null);

    useEffect(() => {
        api.get("/customer/plants")
            .then(r => {
                setPlants(r.data || []);
                setSelectedId(r.data?.[0]?.id || null);
            })
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return <div className="text-zinc-500 text-sm" data-testid="cust-docs-loading">Loading your document vault…</div>;
    }
    if (plants.length === 0) {
        return (
            <div className="bg-white border border-zinc-200 rounded-sm p-8 text-center" data-testid="cust-no-plants">
                <FolderArchive className="w-8 h-8 mx-auto text-zinc-300 mb-3" />
                <div className="text-sm text-zinc-600 font-medium">No plants linked to your account yet.</div>
            </div>
        );
    }

    const selected = plants.find(p => p.id === selectedId) || plants[0];

    return (
        <div className="space-y-6" data-testid="cust-documents-page">
            {/* Header */}
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Document Vault</div>
                    <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900 mt-1">Warranty & handover documents</h1>
                    <div className="text-sm text-zinc-500 mt-1">
                        Warranty cards, invoices, single-line diagrams and site photos for your system.
                    </div>
                </div>
            </div>

            {/* Plant switcher (only shown when multiple plants) */}
            {plants.length > 1 && (
                <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid="cust-docs-plant-switcher">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-2">Select plant</div>
                    <div className="flex flex-wrap gap-2">
                        {plants.map((p, i) => {
                            const active = p.id === selected.id;
                            return (
                                <button
                                    key={p.id}
                                    onClick={() => setSelectedId(p.id)}
                                    className={`px-3 py-2 rounded-sm border text-left transition-colors ${active ? "bg-[#0B0D11] text-white border-[#0B0D11]" : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"}`}
                                    data-testid={`cust-docs-plant-${i}`}
                                >
                                    <div className="text-sm font-medium">{p.customer_name || p.name}</div>
                                    <div className={`text-[10px] font-mono mt-0.5 ${active ? "text-zinc-300" : "text-zinc-500"}`}>
                                        {p.location} · {p.installed_capacity_kw} kWp
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Plant info + Document Vault */}
            <div className="bg-white border border-zinc-200 rounded-sm p-5">
                <div className="flex items-center gap-2 mb-4 pb-4 border-b border-zinc-200">
                    <MapPin className="w-4 h-4 text-zinc-400" />
                    <div>
                        <div className="text-sm font-semibold text-zinc-900">{selected.customer_name || selected.name}</div>
                        <div className="text-xs text-zinc-500 font-mono">{selected.location} · {selected.installed_capacity_kw} kWp</div>
                    </div>
                </div>
                <DocumentVault stationId={selected.id} />
            </div>
        </div>
    );
}
